import ConfigurationUpsertPage from "src/pages/configuration/Upsert";
import SystemLogsListPage from "src/pages/systemLogs/List";
import MainLayout from "../layouts/main";
import AuthGuard from "../guards/AuthGuard";
import PermissionGuard from "../guards/PermissionGuard";
import AuthLayout from "src/layouts/auth";
import LoadingScreen from "../components/LoadingScreen";
import CompanyWizzardLayout from "src/layouts/companyWizard";
import useAuth from "../hooks/useAuth";
import { Suspense, lazy, ElementType, useEffect, useState } from "react";
import {
    Navigate,
    matchPath,
    useRoutes,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import {
    PATH_LOGIN,
    PATH_LOGOUT,
    PATH_RESET_PASSWORD,
    PATH_RESET_PASSWORD_CONFIRMED,
    PATH_NEW_PASSWORD,
    PATH_NEW_PASSWORD_CONFIRMED,
    PATH_CHOOSE_ORGANISATION,
    PATH_REGISTER,
    PATH_REGISTER_INVITE,
    PATH_REGISTER_CONFIRMED,
    PATH_DASHBOARD,
    PATH_REGISTER_VERIFIED,
    PATH_MY_ACCOUNT,
    PATH_CUSTOMERS,
    PATH_CUSTOMERS_ADD,
    PATH_CUSTOMER_GROUPS,
    PATH_CUSTOMER_GROUPS_ADD,
    PATH_SALES_SESSIONS,
    PATH_USERS,
    PATH_USERS_ADD,
    PATH_USERS_EDIT,
    PATH_CUSTOMERS_EDIT,
    PATH_CUSTOMER_GROUPS_EDIT,
    PATH_STATIONS,
    PATH_STATIONS_ADD,
    PATH_LOCATIONS,
    PATH_LOCATIONS_ADD,
    PATH_LOCATIONS_EDIT,
    PATH_TARIFFS,
    PATH_TARIFFS_ADD,
    PATH_TARIFFS_COPY,
    PATH_TARIFFS_EDIT,
    PATH_CHANGE_ORGANISATION,
    PATH_STATION_DETAILS,
    PATH_CONFIGURATION,
    PATH_SYSTEM_LOGS,
    PATH_ACCOUNT_BILLING,
    PATH_STATIONS_EDIT,
    PATH_404,
    PATH_MAINTENANCE,
    PATH_REGISTER_COMPANY,
    PATH_REGISTER_COMPANY_STEP,
    PATH_EMPLOYEE,
    PATH_EMPLOYEE_ADD,
    PATH_EMPLOYEE_EDIT,
    PATH_CUSTOMER_AKA_USER_ADD,
    PATH_CUSTOMER_AKA_USER_EDIT,
    PATH_SALES_PAYOFF,
    PATH_ICONS_LIST,
} from "./paths";
import { useProfileUserPermissions } from "src/modules/profile/hooks/Profile/useUserPermissions";
import { UserActionTypes, useUserDispatch } from "src/contexts/UserContext";
import { Storage } from "src/utils/storage";
import useWss from "src/hooks/useWss";
import CommonLayout from "src/layouts/common";
import PageMaintenance from "src/pages/PageMaintenance";
import UpsertCustomerAkaUserPage from "src/pages/user/UpsertCustomerAkaUser";
import { UserRoles } from "src/modules/user/types/UserRoles";

const Loadable = (Component: ElementType) => (props: any) => {
    const { pathname } = useLocation();

    const { isAuthenticated } = useAuth();

    const isDashboard = pathname.includes("/") && isAuthenticated();

    return (
        <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
            <Component {...props} />
        </Suspense>
    );
};

const withMainGuards = () => (
    <AuthGuard>
        <PermissionGuard>
            <MainLayout />
        </PermissionGuard>
    </AuthGuard>
);

export default function Router() {
    const navigate = useNavigate();
    const { isAuthenticated, userState } = useAuth();
    const dispatch = useUserDispatch();
    const { pathname } = useLocation();
    const [signedIn, setSignedIn] = useState(false);
    const [searchParams] = useSearchParams();
    const searchParamsString = searchParams.toString();

    const { initializeWss, closeWss } = useWss();

    const { data: permissions, refetch: refetchPermissions } = useProfileUserPermissions();

    useEffect(() => {
        if (userState && userState?.company && !userState.roles && !userState.fullAccess) {
            refetchPermissions();
            initializeWss();
        }
    }, [userState]);

    useEffect(() => {
        if (permissions) {
            dispatch({
                type: UserActionTypes.SET_ROLES,
                payload: { ...permissions },
            });
            Storage.setItem("userRoles", permissions);
        }
    }, [permissions]);

    useEffect(() => {
        const isSubscriptionInactive =
            typeof permissions?.isCompanySubscriptionActive === "boolean"
                ? permissions.isCompanySubscriptionActive === false
                : userState?.company?.isSubscriptionActive === false;

        if (!isAuthenticated() || !isSubscriptionInactive) {
            return;
        }

        const routeIsWhitelisted =
            !!matchPath(PATH_CHANGE_ORGANISATION, pathname) ||
            pathname === PATH_LOGOUT ||
            pathname === PATH_MY_ACCOUNT ||
            pathname.includes(PATH_REGISTER);

        if (routeIsWhitelisted) {
            return;
        }

        const sourcePermissions = permissions || userState?.roles;

        if (!sourcePermissions) {
            refetchPermissions();
            return;
        }

        const canManageInvoices = sourcePermissions?.roles?.includes(
            UserRoles.ROLE_CAN_MANAGE_INVOICES
        );
        const hasBillingAccess = !!sourcePermissions?.hasFullAccess || !!canManageInvoices;

        if (hasBillingAccess) {
            if (pathname !== PATH_ACCOUNT_BILLING || searchParams.get("tab") !== "0") {
                navigate(`${PATH_ACCOUNT_BILLING}?tab=0`, { replace: true });
            }
            return;
        }

        if (
            pathname !== PATH_CHOOSE_ORGANISATION ||
            searchParams.get("reason") !== "inactive-company-no-access"
        ) {
            navigate(`${PATH_CHOOSE_ORGANISATION}?reason=inactive-company-no-access`, {
                replace: true,
            });
        }
    }, [isAuthenticated, userState, permissions, pathname, searchParams]);

    useEffect(() => {
        if (isAuthenticated() !== signedIn) {
            setSignedIn(isAuthenticated());
            if (isAuthenticated()) {
                if (pathname !== PATH_LOGIN && pathname !== PATH_DASHBOARD && pathname !== "/") {
                    navigate(pathname + (searchParamsString ? "?" + searchParamsString : ""));
                } else {
                    navigate(PATH_DASHBOARD);
                }
            } else {
                navigate(PATH_LOGIN);
            }
        }
        if (!isAuthenticated() && signedIn) {
            setSignedIn(false);
            closeWss();
            navigate(PATH_LOGOUT);
        }
    }, [isAuthenticated, signedIn]);

    return useRoutes([
        {
            path: "",
            children: [
                {
                    element: (
                        <Navigate to={isAuthenticated() ? PATH_DASHBOARD : PATH_LOGIN} replace />
                    ),
                    index: true,
                },
            ],
        },

        {
            path: PATH_LOGIN,
            element: <AuthLayout />,
            children: [
                {
                    path: "",
                    element: <LoginPage />,
                },
            ],
        },
        {
            path: PATH_CHOOSE_ORGANISATION,
            element: (
                <AuthGuard>
                    <AuthLayout />
                </AuthGuard>
            ),
            children: [{ path: "", element: <ChooseOrganisationPage /> }],
        },
        {
            path: PATH_RESET_PASSWORD,
            element: <AuthLayout />,
            children: [
                {
                    path: "",
                    element: <ResetPasswordPage />,
                },
                {
                    path: PATH_RESET_PASSWORD_CONFIRMED,
                    element: <ResetPasswordConfirmedPage />,
                },
            ],
        },
        {
            path: PATH_NEW_PASSWORD,
            element: <AuthLayout />,
            children: [
                {
                    path: "",
                    element: <NewPasswordPage />,
                },
                {
                    path: PATH_NEW_PASSWORD_CONFIRMED,
                    element: <NewPasswordConfirmedPage />,
                },
            ],
        },
        {
            path: PATH_REGISTER,
            element: <AuthLayout />,
            children: [
                {
                    path: "",
                    element: <RegisterPage />,
                },
                {
                    path: PATH_REGISTER_INVITE,
                    element: <RegisterInvitePage />,
                },
                {
                    path: PATH_REGISTER_CONFIRMED,
                    element: <RegisterConfirmedPage />,
                },
                {
                    path: PATH_REGISTER_VERIFIED,
                    element: <RegisterVerifiedPage />,
                },
            ],
        },
        {
            path: PATH_REGISTER_COMPANY,
            element: (
                <AuthGuard>
                    <CompanyWizzardLayout>
                        <RegisterCompanyPage />
                    </CompanyWizzardLayout>
                </AuthGuard>
            ),
            children: [
                {
                    path: PATH_REGISTER_COMPANY_STEP,
                    element: <RegisterCompanyPage />,
                },
            ],
        },
        {
            path: PATH_CHANGE_ORGANISATION,
            element: <ChangeOrganisationPage />,
        },
        {
            path: PATH_LOGOUT,
            element: <LogoutPage />,
        },
        {
            path: PATH_DASHBOARD,
            element: withMainGuards(),
            children: [
                {
                    path: "",
                    element: <DashboardPage />,
                },
            ],
        },
        {
            path: PATH_SALES_SESSIONS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_SALES_SESSIONS,
                    element: <SessionsPage />,
                },
            ],
        },
        {
            path: PATH_SALES_PAYOFF,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_SALES_PAYOFF,
                    element: <PayoffListPage />,
                },
            ],
        },
        {
            path: PATH_TARIFFS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_TARIFFS,
                    element: <TariffListPage />,
                },
                {
                    path: PATH_TARIFFS_ADD,
                    element: <TariffUpsertPage />,
                },
                {
                    path: PATH_TARIFFS_COPY,
                    element: <TariffUpsertPage />,
                },
                {
                    path: PATH_TARIFFS_EDIT,
                    element: <TariffUpsertPage />,
                },
            ],
        },
        {
            path: PATH_CUSTOMERS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_CUSTOMERS,
                    element: <CustomerListPage />,
                },
                {
                    path: PATH_CUSTOMERS_ADD,
                    element: <CustomerUpsertPage />,
                },
                {
                    path: PATH_CUSTOMERS_EDIT,
                    element: <CustomerUpsertPage />,
                },
            ],
        },
        {
            path: PATH_EMPLOYEE,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_EMPLOYEE,
                    element: <EmployeeListPage />,
                },
                {
                    path: PATH_EMPLOYEE_ADD,
                    element: <EmployeeUpsertPage />,
                },
                {
                    path: PATH_EMPLOYEE_EDIT,
                    element: <EmployeeUpsertPage />,
                },
            ],
        },
        {
            path: PATH_USERS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_USERS,
                    element: <UserListPage />,
                },
                {
                    path: PATH_USERS_ADD,
                    element: <InviteUserPage />,
                },
                {
                    path: PATH_CUSTOMER_AKA_USER_EDIT,
                    element: <UpsertCustomerAkaUserPage />,
                },
                {
                    path: PATH_CUSTOMER_AKA_USER_ADD,
                    element: <UpsertCustomerAkaUserPage />,
                },

                {
                    path: PATH_USERS_EDIT,
                    element: <EditUserPage />,
                },
            ],
        },
        {
            path: PATH_CONFIGURATION,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_CONFIGURATION,
                    element: <ConfigurationUpsertPage />,
                },
            ],
        },
        {
            path: PATH_ACCOUNT_BILLING,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_ACCOUNT_BILLING,
                    element: <AccountBillingDetailsPage />,
                },
            ],
        },
        {
            path: PATH_SYSTEM_LOGS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_SYSTEM_LOGS,
                    element: <SystemLogsListPage />,
                },
            ],
        },
        {
            path: PATH_CUSTOMER_GROUPS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_CUSTOMER_GROUPS,
                    element: <CustomerGroupListPage />,
                },
                {
                    path: PATH_CUSTOMER_GROUPS_ADD,
                    element: <CustomerGroupUpsertPage />,
                },
                {
                    path: PATH_CUSTOMER_GROUPS_EDIT,
                    element: <CustomerGroupUpsertPage />,
                },
            ],
        },
        {
            path: PATH_LOCATIONS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_LOCATIONS,
                    element: <LocationListPage />,
                },
                {
                    path: PATH_LOCATIONS_ADD,
                    element: <LocationUpsertPage />,
                },
                {
                    path: PATH_LOCATIONS_EDIT,
                    element: <LocationUpsertPage />,
                },
            ],
        },
        {
            path: PATH_STATIONS,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_STATIONS,
                    element: <StationListPage />,
                },
                {
                    path: PATH_STATIONS_EDIT,
                    element: <StationUpsertPage />,
                },
                {
                    path: PATH_STATIONS_ADD,
                    element: <StationUpsertPage />,
                },
                {
                    path: PATH_STATION_DETAILS,
                    element: <StationDetailsPage />,
                },
            ],
        },
        {
            path: PATH_MY_ACCOUNT,
            element: withMainGuards(),
            children: [
                {
                    path: PATH_MY_ACCOUNT,
                    element: <MyAccountPage />,
                },
            ],
        },
        {
            path: PATH_404,
            element: <CommonLayout />,
            children: [
                {
                    path: "",
                    element: <NotFoundPage />,
                },
            ],
        },
        {
            path: PATH_MAINTENANCE,
            element: <CommonLayout />,
            children: [
                {
                    path: "",
                    element: <PageMaintenance />,
                },
            ],
        },
        { path: "*", element: <Navigate to="/404" replace /> },
        { path: PATH_ICONS_LIST, element: <IconsListPage /> },
    ]);
}

// Profile
const MyAccountPage = Loadable(lazy(() => import("../pages/profile/MyAccount")));

// Location
const LocationListPage = Loadable(lazy(() => import("../pages/location/List")));
const LocationUpsertPage = Loadable(lazy(() => import("../pages/location/Upsert")));

// Station
const StationListPage = Loadable(lazy(() => import("../pages/station/List")));
const StationUpsertPage = Loadable(lazy(() => import("../pages/station/Upsert")));
const StationDetailsPage = Loadable(lazy(() => import("../pages/station/Details")));

// Customer
const CustomerListPage = Loadable(lazy(() => import("../pages/customer/List")));
const CustomerUpsertPage = Loadable(lazy(() => import("../pages/customer/Upsert")));

// Employee
const EmployeeListPage = Loadable(lazy(() => import("../pages/employee/List")));
const EmployeeUpsertPage = Loadable(lazy(() => import("../pages/employee/Upsert")));

// Group
const CustomerGroupListPage = Loadable(lazy(() => import("../pages/group/List")));
const CustomerGroupUpsertPage = Loadable(lazy(() => import("../pages/group/Upsert")));

// Tariff
const TariffListPage = Loadable(lazy(() => import("../pages/tariff/List")));
const TariffUpsertPage = Loadable(lazy(() => import("../pages/tariff/Upsert")));

// User
const InviteUserPage = Loadable(lazy(() => import("../pages/user/Invite")));
const EditUserPage = Loadable(lazy(() => import("../pages/user/Edit")));
const AccountBillingDetailsPage = Loadable(lazy(() => import("src/pages/account/Details")));

// Sales
const SessionsPage = Loadable(lazy(() => import("../pages/session/List")));
const UserListPage = Loadable(lazy(() => import("../pages/user/List")));
const PayoffListPage = Loadable(lazy(() => import("../pages/payoff/List")));

// Auth
const LoginPage = Loadable(lazy(() => import("../pages/auth/Login")));
const LogoutPage = Loadable(lazy(() => import("../pages/auth/Logout")));
const ResetPasswordPage = Loadable(lazy(() => import("../pages/auth/ResetPassword")));
const ResetPasswordConfirmedPage = Loadable(
    lazy(() => import("../pages/auth/ResetPasswordConfirmed"))
);
const NewPasswordPage = Loadable(lazy(() => import("../pages/auth/NewPassword")));
const NewPasswordConfirmedPage = Loadable(lazy(() => import("../pages/auth/NewPasswordConfirmed")));
const ChooseOrganisationPage = Loadable(lazy(() => import("../pages/auth/ChooseOrganisation")));
const ChangeOrganisationPage = Loadable(lazy(() => import("../pages/auth/ChangeOrganisation")));
const RegisterPage = Loadable(lazy(() => import("../pages/auth/Register")));
const RegisterInvitePage = Loadable(lazy(() => import("../pages/auth/RegisterInvite")));
const RegisterConfirmedPage = Loadable(lazy(() => import("../pages/auth/RegisterConfirmed")));
const RegisterVerifiedPage = Loadable(lazy(() => import("../pages/auth/RegisterVerify")));

// Company Wizzard
const RegisterCompanyPage = Loadable(lazy(() => import("../pages/auth/RegisterCompany")));

// const CompanyWizzardAccountTypePage = Loadable(
//     lazy(() => import("../pages/auth/NewCompanyWizzard/AccountType"))
// );

// const CompanyWizzardCompanyPage = Loadable(
//     lazy(() => import("../pages/auth/CompanyWizzard/Company"))
// );
// const CompanyWizzardSettingsPage = Loadable(
//     lazy(() => import("../pages/auth/CompanyWizzard/Settings"))
// );
// const CompanyWizzardTariffPage = Loadable(
//     lazy(() => import("../pages/auth/CompanyWizzard/Tariff"))
// );
// const CompanyWizzardLocationPage = Loadable(
//     lazy(() => import("../pages/auth/CompanyWizzard/Location"))
// );
// const CompanyWizzardStationPage = Loadable(
//     lazy(() => import("../pages/auth/CompanyWizzard/Station"))
// );
// const CompanyWizzardCustomersPage = Loadable(
//     lazy(() => import("../pages/auth/CompanyWizzard/Customers"))
// );
// const CompanyWizzardSuccessPage = Loadable(
//     lazy(() => import("../pages/auth/CompanyWizzard/Success"))
// );

// Dashboard
const DashboardPage = Loadable(lazy(() => import("../pages/dashboard/Dashboard")));

// Other
const NotFoundPage = Loadable(lazy(() => import("../pages/Page404")));

// Icon Grid
const IconsListPage = Loadable(lazy(() => import("../theme/overrides/CustomIcons")));
